<template>
  <v-form v-if="!status">
    <v-text-field
      label="Name"
      v-model="name"
      clearable
      :error-messages="nameErrorMsg"
      @change="validateName"
      bg-color="transparent"
    />
    <AddressAutocompleteInput
      :initialValue="address"
      @update="updateAddress"
      id="favLocationAddress"
      :errorMsg="addressErrorMsg"
      class="mb-5"
      label="Address"
    />
    <v-card rounded="lg" flat class="pa-5 mb-5">
      <p class="text-h6">Location privacy settings</p>
      <v-select
        v-model="locationType"
        label="Location Type"
        :items="['Work', 'Home', 'Depot', 'Other']"
        bg-color="transparent"
      ></v-select>
      <v-select
        v-if="featureStore.can('location-visibility')"
        v-model="visibility"
        label="Visibility"
        :items="['private', 'fleet']"
        bg-color="transparent"
      ></v-select>
    </v-card>
    <v-card-actions class="px-0 overflow-hidden">
      <CustomOutlinedButton @click="emit('close')" shape="box">
        Cancel
      </CustomOutlinedButton>
      <div class="flex-grow-1 flex-shrink-1">
        <CustomSolidButton full-width @click="saveFavLocation">
          {{
            initialValue?.externalId
              ? "Update location"
              : "Save as a favourite location"
          }}
        </CustomSolidButton>
      </div>
    </v-card-actions>
  </v-form>
  <v-alert type="success" v-else-if="status === 'SUCCESS'" class="mx-2">
    Favourite location was created successfully and saved to the database
  </v-alert>
  <v-alert type="error" v-else-if="status === 'FAILED'" class="mx-2">
    Favourite location was created locally but failed to be saved to the
    database
  </v-alert>
</template>
<script lang="ts" setup>
import { onBeforeUnmount, onMounted, ref } from "vue";

import CustomOutlinedButton from "@/components/ui-elements/buttons/custom-buttons/CustomOutlinedButton.vue";
import CustomSolidButton from "@/components/ui-elements/buttons/custom-buttons/CustomSolidButton.vue";
import AddressAutocompleteInput from "@/domains/addressLookups/AddressAutocompleteInput.vue";
import type {
  AddressAutocompleteInputUpdateObj,
  processedAddressObj,
} from "@/domains/addressLookups/types.ts";
import { useFleetStore } from "@/domains/fleet/fleetStore.ts";
import SavedLocation from "@/domains/locations/savedLocation.ts";
import { useSavedLocationStore } from "@/domains/locations/savedLocationStore.ts";
import { useFeatureStore } from "@/stores/featureStore.ts";

const savedLocationStore = useSavedLocationStore();
const featureStore = useFeatureStore();
const fleetStore = useFleetStore();

const emit = defineEmits<{
  (e: "update", value: SavedLocation): void;
  (e: "close"): void;
}>();

const { initialValue } = defineProps<{
  initialValue?: SavedLocation | undefined;
}>();

const status = ref<"PROCESSING" | "SUCCESS" | "FAILED" | null>(null);
// form data
const name = ref<string | null | undefined>(undefined);
const nameErrorMsg = ref<string | null>(null);
const address = ref<processedAddressObj | undefined>(undefined);
const addressErrorMsg = ref<string | null>(null);
const locationType = ref<"Work" | "Home" | "Depot" | "Other">("Home");
const visibility = ref<"private" | "fleet">("private");

onMounted(() => {
  if (initialValue) {
    // location details
    name.value = initialValue.name;
    address.value = {
      address: initialValue.address ?? "Unknown Address",
      coordinates: initialValue.coordinates,
    };
    // privacy and visibility settings
    locationType.value = initialValue.type;
    visibility.value = initialValue.visibility;
  }
});

onBeforeUnmount(() => {
  name.value = undefined;
  nameErrorMsg.value = null;
  address.value = undefined;
  addressErrorMsg.value = null;
  status.value = null;
});

// validation methods

/**
 * Validates the name input and sets an error message if the name is empty.
 *
 * @return {boolean} Returns true if the name is not empty, false otherwise.
 */
function validateName(): boolean {
  nameErrorMsg.value = null;
  if (!name.value) {
    nameErrorMsg.value = "Please enter a name";
  }
  return !!name.value;
}

/**
 * Validates the address input and sets an error message if the address is empty.
 *
 * @return {boolean} Returns true if the address is not empty, false otherwise.
 */
function validateAddress(): boolean {
  addressErrorMsg.value = null;
  if (!address.value) {
    addressErrorMsg.value = "Please enter an address";
  }
  return !!address.value;
}

// event handlers

/**
 * Updates the address based on the input data and validates the address.
 *
 * @param {AddressAutocompleteInputUpdateObj} address - The updated address data.
 * @return {void} This function does not return anything.
 */
function updateAddress(addressValue: AddressAutocompleteInputUpdateObj): void {
  address.value = addressValue.addressData ?? undefined;
  validateAddress();
}

/**
 * Resets the name, address, and error messages to default values and emits a close event after a delay.
 *
 * @return {void} This function does not return anything.
 */
function closeAndReset(): void {
  name.value = undefined;
  nameErrorMsg.value = null;
  address.value = undefined;
  addressErrorMsg.value = null;
  setTimeout(() => {
    emit("close");
    status.value = null;
  }, 1000);
}

/**
 * Saves the favourite location after validating the name and address inputs.
 *
 * @return {Promise<void>} This function does not return anything.
 */
async function saveFavLocation(): Promise<void> {
  status.value = "PROCESSING";

  const isNameValid = validateName();
  const isAddressValid = validateAddress();

  if (!isNameValid || !isAddressValid) {
    status.value = null;
    return;
  }

  const coordinates = address.value?.coordinates;

  if (!coordinates) {
    status.value = "FAILED";
    return;
  }

  // `locations` gained `name`/`type` columns via a later migration - see
  // savedLocation.ts's fromDTO/isSavedLocationType. `visibility` still has
  // no backing column, so it's collected in the UI below but not sent.
  const persistedLocationData = {
    address: address.value!.address,
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
    name: name.value ?? undefined,
    type: locationType.value,
  };

  // A prefilled-but-unpersisted `initialValue` (e.g. from
  // `LocationRequestModal`'s `processedAddressObj` passthrough) has no
  // `externalId` - only a real, already-saved location does, so that's what
  // distinguishes "editing this location" from "creating a new one with a
  // head start on the address".
  let result;
  if (initialValue?.externalId) {
    result = await savedLocationStore.updateSavedLocation(
      initialValue.localId,
      persistedLocationData,
    );
  } else {
    // `locations`' RLS insert policy rejects a row with no `fleet_id`
    // (confirmed live: 42501 "new row violates row-level security policy")
    // the same way `route_plans` needed an explicit `fleet_id` - so it's
    // supplied here rather than left out, mirroring `tripCoreClass.ts`'s
    // `saveTrip`.
    const fleetId = fleetStore.fleetId;

    if (!fleetId) {
      status.value = "FAILED";
      return;
    }

    result = await savedLocationStore.createSavedLocation({
      ...persistedLocationData,
      fleet_id: fleetId,
    });
  }

  status.value = result.success ? "SUCCESS" : "FAILED";

  closeAndReset();
}
</script>
<style scoped>
* :deep(.v-slider--horizontal) {
  margin-left: unset;
  margin-right: unset;
}

* :deep(.v-slider__track-fill) {
  border-radius: 2px; /* override default slider border-radius */
}

* :deep(.v-slider__track-background) {
  border-radius: 2px; /* override default slider border-radius */
}

:deep(.v-field__input > input) {
  padding-left: 16px !important;
}
:deep(.v-field__input) {
  padding-left: 16px !important;
}
</style>
